import { Plus } from "lucide-react";
import { type MouseEvent, useContext, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button, buttonVariants } from "@/app/components/ui/button";
import {
	nextAfterWorkHistory,
	previousBeforeWorkHistory,
	previousBeforeWorkHistoryTitle,
	SKIP_BACK_FROM_WORK_HISTORY_SUMMARY,
	skipFromWorkHistorySummary,
	useEducationFirst,
} from "@/app/router/sectionOrder";
import type { ResumeContextValue } from "@/app/types/resume";
import tips from "../../../../assets/copy/tips";
import Loader from "../../../../components/Loader";
import SummaryBox from "../../../../components/ResumeBuilder/SummaryBox";
import Tips from "../../../../components/Tips/Tips";
import ResumeContext from "../../../../context/ResumeContext";
import { NavigationHandler } from "../../../../handlers/navigationHandler";

const WorkHistorySummary = () => {
	const navigate = useNavigate();

	const navigationHandler = new NavigationHandler();
	let nextLink: string;
	const link = useLocation().pathname.split("/")[2];

	const { resumeData } = useContext(ResumeContext) as ResumeContextValue;

	const { education, workHistory, journal } = resumeData;
	const [loading, setLoading] = useState(true);

	const resumeCurrentStatus = journal ? journal.currentStatus : false;
	const isComplete = resumeCurrentStatus == "COMPLETED";

	const educationFirst = useEducationFirst();

	// Skills for a reader with no experience — they were asked for education before
	// this screen. Everyone else gets education next.
	const nextSection = nextAfterWorkHistory(educationFirst, {
		educationCount: education.length,
		workHistoryCount: workHistory.length,
	});

	const nextTxt = "Continue";

	// `journal` only exists once the server resume has hydrated into the context.
	// Until then `workHistory` is the blank placeholder row from `resumedata.js`,
	// whose empty jobTitle is indistinguishable from a half-finished entry — so
	// opening this screen directly, or reloading it, bounced the reader to the
	// empty "add a job" form even with jobs already saved.
	const hasLoadedResume = Boolean(journal);

	useEffect(() => {
		if (!hasLoadedResume) return;

		if (workHistory.length > 0 && workHistory[0].jobTitle === "") {
			navigate("/resume/work-history");
		} else {
			return setLoading(false);
		}
	}, [workHistory, hasLoadedResume, navigate]);

	function next(e: MouseEvent<HTMLAnchorElement>) {
		e.preventDefault();

		const skipOne = skipFromWorkHistorySummary(educationFirst, education.length);

		nextLink = navigationHandler.getNext(navigationHandler.template.navigationFlow, link, isComplete, skipOne).value
			.url;

		if (nextLink === "review") return navigate("/my-resumes");
		return navigate(`/resume/${nextLink}`);
	}

	// Target and label both come off `educationFirst`, so the button can never
	// advertise a different screen than the one it opens.
	const previousSection = previousBeforeWorkHistory(educationFirst, {
		educationCount: education.length,
		workHistoryCount: workHistory.length,
	});
	const previousTxt = `Previous : ${previousBeforeWorkHistoryTitle(educationFirst)}`;

	function previous(e: MouseEvent<HTMLAnchorElement>) {
		e.preventDefault();

		navigationHandler.getPrevious(
			navigationHandler.template.navigationFlow,
			link,
			isComplete,
			SKIP_BACK_FROM_WORK_HISTORY_SUMMARY,
		);
		return navigate(`/resume/${previousSection}`);
	}

	const addAnotherEduHandler = () => {
		navigationHandler.getPrevious(navigationHandler.template.navigationFlow, link, isComplete, 0);

		return navigate("/resume/work-history");
	};

	const summaryBox = workHistory.map((summaryItem, index) => (
		<SummaryBox item={summaryItem} key={index} index={summaryItem.referenceId} />
	));

	useEffect(() => {
		if (!hasLoadedResume) return;

		if (!loading && workHistory.length === 0) {
			navigate("/resume/work-history");
		}
	}, [workHistory, hasLoadedResume, loading, navigate]);

	return (
		<div className="min-h-screen">
			{loading ? (
				<Loader />
			) : (
				<div className="w-full lg:w-2/3">
					<div className="mb-2 flex justify-end">
						<Tips content={tips.workHistory} />
					</div>
					{summaryBox}

					<Button type="button" variant="link" className="cursor-pointer font-semibold" onClick={addAnotherEduHandler}>
						<Plus className="mr-3 size-4" />
						Add another position
					</Button>
					<div className="mt-6 flex items-center justify-between">
						<Link to={`/resume/${previousSection}`} className={buttonVariants({ variant: "ghost" })} onClick={previous}>
							{previousTxt}
						</Link>
						{/* `next` preventDefaults and navigates through the flow, so `to` only
						    shows up in the status bar and on ctrl/middle-click — which React
						    Router hands straight to the browser. Keep it on the same target the
						    flow picks, or those clicks land on a different section. */}
						<Link to={`/resume/${nextSection}`} className={buttonVariants()} onClick={next}>
							{nextTxt}
						</Link>
					</div>
				</div>
			)}
		</div>
	);
};

export default WorkHistorySummary;
